import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { DollarSign, Clock, CalendarCheck, Users, TrendingUp, MapPin } from "lucide-react";
import { useModules } from "@/context/ModulesContext";

const StatCard = ({ label, value, icon: Icon, accent, testid }) => (
  <Card
    data-testid={testid}
    className="p-6 border-border rounded-lg hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 bg-card"
  >
    <div className="flex items-start justify-between">
      <div className="overline text-muted-foreground">{label}</div>
      <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="h-[18px] w-[18px] text-white" />
      </div>
    </div>
    <div className="font-display text-3xl font-black tracking-tight mt-4">{value}</div>
  </Card>
);

const pipelineLabels = { new: "New", contacted: "Contacted", quoted: "Quoted", won: "Won", lost: "Lost" };
const pipelineColors = {
  new: "bg-accent",
  contacted: "bg-[#E9C46A]",
  quoted: "bg-secondary",
  won: "bg-primary",
  lost: "bg-muted-foreground",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const { isVisible } = useModules();

  useEffect(() => {
    api.get("/dashboard/stats").then((res) => setStats(res.data));
  }, []);

  if (!stats)
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );

  const maxPipeline = Math.max(1, ...Object.values(stats.lead_pipeline));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {isVisible("dash.stat_revenue") && <StatCard label="Revenue (Paid)" value={money(stats.revenue)} icon={DollarSign} accent="bg-primary" testid="stat-revenue" />}
        {isVisible("dash.stat_outstanding") && <StatCard label="Outstanding" value={money(stats.outstanding)} icon={Clock} accent="bg-secondary" testid="stat-outstanding" />}
        {isVisible("dash.stat_jobs_week") && <StatCard label="Jobs This Week" value={stats.jobs_week} icon={CalendarCheck} accent="bg-accent" testid="stat-jobs-week" />}
        {isVisible("dash.stat_customers") && <StatCard label="Active Customers" value={stats.customers} icon={Users} accent="bg-[#2D6A4F]" testid="stat-customers" />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {isVisible("dash.revenue_trend") && <Card className="lg:col-span-2 p-6 border-border rounded-lg bg-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline text-muted-foreground">Last 6 months</div>
              <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">Revenue Trend</h3>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4 text-primary" /> {stats.close_rate}% close rate
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.monthly_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E2DC" vertical={false} />
              <XAxis dataKey="month" tickLine={false} axisLine={false} style={{ fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} style={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                cursor={{ fill: "rgba(30,63,32,0.06)" }}
                formatter={(v) => [money(v), "Revenue"]}
                contentStyle={{ borderRadius: 8, border: "1px solid #E2E2DC" }}
              />
              <Bar dataKey="revenue" fill="#1E3F20" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </Card>}

        {isVisible("dash.lead_pipeline") && <Card className="p-6 border-border rounded-lg bg-card">
          <div className="overline text-muted-foreground">Pipeline</div>
          <h3 className="font-display text-lg font-bold tracking-tight mt-0.5 mb-6">Leads by Stage</h3>
          <div className="space-y-4">
            {Object.entries(stats.lead_pipeline).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium">{pipelineLabels[k]}</span>
                  <span className="text-muted-foreground tabular-nums">{v}</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full ${pipelineColors[k]} transition-all duration-500`}
                    style={{ width: `${(v / maxPipeline) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>}
      </div>

      {isVisible("dash.upcoming_jobs") && <Card className="p-6 border-border rounded-lg bg-card">
        <div className="overline text-muted-foreground">Upcoming</div>
        <h3 className="font-display text-lg font-bold tracking-tight mt-0.5 mb-5">Next Jobs</h3>
        {stats.upcoming_jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No upcoming jobs scheduled yet.</p>
        ) : (
          <div className="space-y-2">
            {stats.upcoming_jobs.map((j) => (
              <div
                key={j.id}
                data-testid="upcoming-job"
                className="flex items-center gap-4 p-3 rounded-md border border-border hover:bg-muted/50 transition-colors duration-200"
              >
                <div className="h-10 w-10 rounded-md bg-primary/10 flex flex-col items-center justify-center text-primary shrink-0">
                  <span className="text-[0.65rem] font-semibold uppercase leading-none">
                    {new Date(j.scheduled_date + "T00:00").toLocaleDateString("en-US", { month: "short" })}
                  </span>
                  <span className="font-display font-bold leading-none text-sm">
                    {new Date(j.scheduled_date + "T00:00").getDate()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{j.title}</div>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                    {j.customer_name || "—"} · {j.start_time}
                    {j.address && (
                      <span className="flex items-center gap-1 ml-1">
                        <MapPin className="h-3 w-3" /> {j.address}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm font-semibold tabular-nums">{money(j.price)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>}
    </div>
  );
}
